import {
    IconStyle,
    PaintStyle,
    PaletteColorType,
    PREDEFINED_PALETTES,
    ShapeColorStyle,
    ShapeType,
    ThemeStyleWeight
} from "common/constants";
import { builtInFonts } from "common/fontConstants";
import { THEME_DEFAULTS, themes } from "common/themes";
import { getStaticUrl } from "js/config";
import { Palette } from "js/core/models/palette";
import StorageModel from "js/core/storage/storageModel";
import { StyleSheet } from "js/core/styleSheet";
import { formatter } from "common/formatter";
import { _, Backbone } from "js/vendor";
import { getDefaultSlides } from "common/themes/defaultDefaultSlides";
import { AiImagePresetStyles } from "common/aiConstants";
import { fontManager } from "js/core/services/fonts";
import ThemesAdapter from "js/core/storage/themesAdapter";
import themeManager from "../themeManager";
import tinycolor from "tinycolor2";
import { color, getDefaultShapeStyle, StyleSet } from "./ColorHelper";
import { getSequentialRunner } from "common/utils/runSequentially";

export function getThemeUrl(path, style, version) {
    const url = path ? `/themes/v${version}/${path}/${style}.scss` : `/themes/v${version}/${style}.scss`;
    return getStaticUrl(url);
}

const Theme = StorageModel.extend({

    root: "user_themes",

    defaults: THEME_DEFAULTS,

    hasMigrationChanges: false,

    ensurePersisted: true,

    constructor: function(...args) {
        StorageModel.prototype.constructor.call(this, ...args);
        this.runSequentially = getSequentialRunner();
    },

    createAdapter: function(options) {
        return new ThemesAdapter({ ...options, model: this });
    },

    getFontIds: function() {
        return Object.entries(this.attributes)
            .filter(([key, value]) => key.endsWith("FontId"))
            .map(([key, value]) => value);
    },

    getBackgrounds() {
        return this.get("backgrounds") || [];
    },

    getBackground: function(id) {
        return this.getBackgrounds().find(background => background.id == id);
    },

    getStyleSets() {
        return this.styleSets || [];
    },

    getStyleSet: function(id) {
        let styleSet = this.styleSets.find(styleSet => styleSet.id == id);
        if (!styleSet) {
            console.log("Could not find styleSet", id);
            styleSet = this.styleSets[0];
        }
        return styleSet;
    },

    getColors: function() {
        let themeColors = this.get("colors");
        if (!themeColors) {
            let palette = this.getPalette(this.get("palette_name"));
            themeColors = _.clone(palette.colors);
        }
        return themeColors;
    },

    getAccentColorsCount() {
        return Object.keys(this.palette.getAccentColors()).length;
    },

    getPalette: function(paletteId) {
        let palette = _.find(PREDEFINED_PALETTES, { id: paletteId });
        if (!palette) {
            // fallback for old theme support
            palette = _.find(PREDEFINED_PALETTES, { old_id: paletteId });
        }
        if (!palette) {
            palette = _.find(PREDEFINED_PALETTES, { id: "colorful" });
        }
        return _.clone(palette);
    },

    isBespoke: function() {
        return this.has("styleSheet");
    },

    getAiImageStyle: function() {
        const imageStyle = this.get("imageStyle");
        if (!imageStyle) {
            return AiImagePresetStyles[0].style;
        }
        if (imageStyle == "custom") {
            return this.get("imageStylePrompt");
        } else {
            return imageStyle;
        }
    },

    getStyles(version, reloadCache = false) {
        if (!this.getStylesPromises) {
            this.getStylesPromises = {};
        }

        if (reloadCache) {
            delete this.getStylesPromises[version];
        }

        if (!this.getStylesPromises[version]) {
            const getVersionedThemeUrl = (path, style) => getThemeUrl(path, style, version);

            this.getStylesPromises[version] = (async () => {
                const decorationStyles = {
                    outlined: await this.loadStyleSheet(getVersionedThemeUrl("elements", "outlined")),
                    muted: await this.loadStyleSheet(getVersionedThemeUrl("elements", "muted")),
                    filled: await this.loadStyleSheet(getVersionedThemeUrl("elements", "filled")),
                    fillAndStroke: await this.loadStyleSheet(getVersionedThemeUrl("elements", "fillAndStroke")),
                };

                ////// Load frankenstein stylesheet from theme properties //////
                const styleSheet = await this.loadStyleSheet(getVersionedThemeUrl(null, "default"));

                this.loadStyleSheet(getVersionedThemeUrl("backgrounds", this.get("styleBackground")), styleSheet);

                const decoration = this.get("styleDecoration");
                await this.loadStyleSheet(getVersionedThemeUrl("decorations", decoration), styleSheet);

                await this.loadStyleSheet(getVersionedThemeUrl("elements", this.get("styleElementStyle")), styleSheet);

                if (this.get("styleShape") == ShapeType.NONE) {
                    await this.loadStyleSheet(getVersionedThemeUrl("shapes", "shape-none"), styleSheet);
                } else {
                    await this.loadStyleSheet(getVersionedThemeUrl("shapes", "shape"), styleSheet);
                }

                await this.loadStyleSheet(getVersionedThemeUrl("weights", this.get("styleWeight")), styleSheet);

                await this.loadFonts(styleSheet);

                await this.loadStyleSheet(getVersionedThemeUrl("effects", this.get("styleEffect")), styleSheet);

                if (this.has("customStyles")) {
                    await this.mergeCssIntoStylesheet(styleSheet, this.get("customStyles"));
                }

                styleSheet.applyStyleSheetVariables(styleSheet.variables);
                styleSheet.processFillAndStroke();
                //////

                // Apply any variables to the values in the decorationStyles
                for (const style of Object.keys(decorationStyles)) {
                    const decorationStyle = decorationStyles[style];
                    for (const property of Object.keys(decorationStyle)) {
                        const value = decorationStyle[property];
                        if (value && typeof (value) == "string" && value.startsWith("$")) {
                            const varName = value.split(" ")[0];
                            decorationStyle[property] = decorationStyle[property].replace(varName, styleSheet.variables[varName.substr(1)]);
                        }
                    }
                }

                return { styleSheet, decorationStyles };
            })();
        }

        return this.getStylesPromises[version];
    },

    loadTheme(forceReload = false, reloadThemeSlides = true) {
        if (this.themeLoaded && !forceReload) {
            return this.runSequentially(async () => {
            });
        }

        this.themeLoaded = true;
        return this.runSequentially(async () => {
            if (!this.has("colors")) {
                let predefinedPalette = _.find(PREDEFINED_PALETTES, { id: this.get("palette_name") || "colorful" });
                if (predefinedPalette) {
                    this.update({ colors: predefinedPalette.colors }, { replaceKeys: true });
                }
            }

            this.palette = new Palette(this);

            // Ensure collection colors are valid (some collection color records can be corrupted because of a bug)
            if (this.has("collectionColors") && Array.isArray(this.get("collectionColors"))) {
                const validCollectionColors = _.cloneDeep(this.get("collectionColors"))
                    .filter(collectionColor => {
                        if (!collectionColor) {
                            return false;
                        }
                        if (!collectionColor.id) {
                            return false;
                        }
                        if (collectionColor.type === "sequence") {
                            return Array.isArray(collectionColor.colors) && collectionColor.colors.length > 0;
                        }
                        if (collectionColor.type === "gradient") {
                            return !!collectionColor.startColor && !!collectionColor.endColor;
                        }
                        return true;
                    });

                this.update({ collectionColors: validCollectionColors }, { replaceKeys: true });
            }

            // Create collectionColors if it doesn't exist, if it's false it means there are no collection colors defined (which is valid)
            // this should only happen for legacy themes created before collection colors feature was added
            if (!this.has("collectionColors")) {
                this.update({
                    collectionColors: [{
                        id: "colorful",
                        type: "sequence",
                        colors: Object.keys(this.palette.getSlideColors())
                    }]
                }, { replaceKeys: true });
            }

            // create default default slides
            if (!this.has("defaultSlides")) {
                this.update({ defaultSlides: getDefaultSlides() }, { replaceKeys: true });
            }

            await this.migrate();

            if (!this.has("styleSets") || this.get("styleSets").length == 0) {
                this.migrateToStyleSets();
            }

            // convert raw style sets into StyleSet objects
            this.styleSets = this.get("styleSets").map(styleSet => new StyleSet(styleSet));

            await this.loadThemeSlides(reloadThemeSlides);

            if (!this.disconnected) {
                await this.updatePromise;
            }
        });
    },

    async loadThemeSlides(reload = true) {
        const slideLayoutSlideIds = this.get("slideLayouts") ?? [];
        const themeSlideSlideIds = this.get("themeSlides") ?? [];

        this.slideLayouts = [];
        this.themeSlides = [];

        await Promise.all([
            Promise.all(slideLayoutSlideIds.map(async id => {
                const slide = await themeManager.getThemeSlide(id, reload);
                if (slide) {
                    this.slideLayouts.push(slide);
                }
            })),
            Promise.all(themeSlideSlideIds.map(async id => {
                const slide = await themeManager.getThemeSlide(id, reload);
                if (slide) {
                    this.themeSlides.push(slide);
                }
            })),
        ]);

        _.sortBy(this.slideLayouts, "slide.attributes.modifiedAt");
        _.sortBy(this.themeSlides, "slide.attributes.modifiedAt");
    },

    async migrate() {
        // Copy accent colors into bgColors
        if ((this.get("version") || 1) < 2) {
            let colors = _.cloneDeep(this.get("colors") ?? {});
            let defaultBackgroundColor = this.get("defaultBackgroundColor");
            let bgIndex = 1;
            for (let color of Object.keys(colors).sort((a, b) => {
                if (a === "theme") return -1;
                if (b === "theme") return 1;
                const aNum = parseInt(a.replace("accent", ""));
                const bNum = parseInt(b.replace("accent", ""));
                return aNum - bNum;
            })) {
                if (color == "theme" || color.startsWith("accent")) {
                    colors[`bg${bgIndex}`] = {
                        type: "color",
                        value: colors[color],
                        allowColorOnColor: false,
                        foregroundColor: false
                    };
                    bgIndex++;
                }
                if (!colors[color].value) {
                    colors[color] = {
                        type: "color",
                        value: colors[color],
                        allowColorOnColor: false,
                        foregroundColor: false
                    };
                }
            }

            let themeColorIsDark = tinycolor(colors.theme.value).isDark();

            // Migrate old gradients into bgColors
            if (this.has("backgroundGradients") && this.get("backgroundGradients").length > 0) {
                for (let gradient of this.get("backgroundGradients")) {
                    let color = {
                        type: "gradient",
                        value: gradient,
                        oldId: gradient.id,
                    };
                    switch (gradient.colorStyle) {
                        case "dark":
                            color.allowColorOnColor = true;
                            color.foregroundColor = "light";
                            break;
                        case "color":
                            color.allowColorOnColor = false;
                            color.foregroundColor = themeColorIsDark ? "light" : "dark";
                            break;
                        case "light":
                        default:
                            color.allowColorOnColor = true;
                            color.foregroundColor = "dark";
                            break;
                    }
                    colors[`bg${bgIndex}`] = color;

                    if (gradient.id == defaultBackgroundColor) {
                        defaultBackgroundColor = `bg${bgIndex}`;
                    }
                    bgIndex++;
                }
            }

            let collectionColors;
            if (this.has("collectionColors") && this.get("collectionColors").length > 0) {
                collectionColors = _.cloneDeep(this.get("collectionColors"));
                for (let collectionColor of collectionColors) {
                    if (collectionColor.overrideBackgroundColor == "background_light") {
                        collectionColor.foregroundColor = "dark";
                        collectionColor.allowColorOnColor = false;
                    } else if (collectionColor.overrideBackgroundColor == "background_dark") {
                        collectionColor.foregroundColor = "light";
                        collectionColor.allowColorOnColor = false;
                    } else {
                        // auto
                        collectionColor.foregroundColor = null;
                        collectionColor.allowColorOnColor = false;
                    }
                    delete collectionColor.overrideBackgroundColor;
                }
            }

            let backgroundImages;
            if (this.has("backgroundImages")) {
                backgroundImages = _.cloneDeep(this.get("backgroundImages"));
                for (let bgImage of backgroundImages) {
                    if (bgImage.colorStyle == "light") {
                        bgImage.foregroundColor = "dark";
                        bgImage.allowColorOnColor = true;
                    } else if (bgImage.colorStyle == "dark") {
                        bgImage.foregroundColor = "light";
                        bgImage.allowColorOnColor = true;
                    } else if (bgImage.colorStyle == "color") {
                        bgImage.foregroundColor = "dark";
                        bgImage.allowColorOnColor = false;
                    } else {
                        // default to "light" when no colorStyle is specified
                        bgImage.foregroundColor = "dark";
                        bgImage.allowColorOnColor = true;
                    }
                    delete bgImage.colorStyle;
                }
            }

            this.update({ colors, version: 2, backgroundImages, collectionColors, backgroundGradients: null, defaultBackgroundColor });
        }

        //// February 2018 migration to split styleBackground options into defaultBackgroundColor ////
        const styleBackground = this.get("styleBackground");
        const defaultBackgroundColor = this.get("defaultBackgroundColor");
        const removedStyleBackgrounds = {
            "light": PaletteColorType.BACKGROUND_LIGHT,
            "dark": PaletteColorType.BACKGROUND_DARK,
            "color": PaletteColorType.THEME
        };

        if (["gradient", "reverseGradient"].includes(styleBackground) && !defaultBackgroundColor) {
            this.update({ defaultBackgroundColor: "theme" });
            this.hasMigrationChanges = true;
        } else if (removedStyleBackgrounds[styleBackground]) {
            let defaultBackground = removedStyleBackgrounds[styleBackground];
            this.update({
                "defaultBackgroundColor": defaultBackground,
                "styleBackground": "none"
            });
            this.hasMigrationChanges = true;
        }

        //// FONTS MIGRATION OLDEST -> PREVIOUS ////
        if (!this.get("fontHeaderFontId") && (!this.get("fontHeader") || !this.get("fontHeaderRegular"))) {
            const headerFontPath = `${this.get("styleTitleFont")}-header-${this.get("styleFontWeight")}`;
            const styleSheet = await this.loadStyleSheet(getThemeUrl("fonts", headerFontPath, 9));
            this.setObsoleteFontVariables("header", headerFontPath, styleSheet.variables);
        }
        if (!this.get("fontTitleFontId") && (!this.get("fontTitle") || !this.get("fontTitleRegular"))) {
            let titleFont = this.get("styleTitleFont");
            // special case for existing montserrat themes which actually use playfair as title font
            if (titleFont === "playfair") {
                titleFont = "raleway";
            }
            const titleFontPath = `${titleFont}-title-${this.get("styleFontWeight")}`;
            const styleSheet = await this.loadStyleSheet(getThemeUrl("fonts", titleFontPath, 9));
            this.setObsoleteFontVariables("title", titleFontPath, styleSheet.variables);
        }
        if (!this.get("fontBodyFontId") && (!this.get("fontBody") || !this.get("fontBodyRegular"))) {
            const bodyFontPath = `${this.get("styleBodyFont")}-body`;
            const styleSheet = await this.loadStyleSheet(getThemeUrl("fonts", bodyFontPath, 9));
            this.setObsoleteFontVariables("body", bodyFontPath, styleSheet.variables);
        }

        //// FONTS MIGRATION PREVIOUS -> CURRENT ////
        for (const style of ["Header", "Title", "Body"]) {
            if (!this.get(`font${style}FontId`)) {
                // Marking so we have changes first
                this.hasMigrationChanges = true;

                const regularFontFaceName = this.get(`font${style}Regular`);
                const emphasizedFontFaceName = this.get(`font${style}Emphasized`);

                // Looking up built in font
                for (const [fontId, fontDefinition] of Object.entries(builtInFonts)) {
                    for (const fontStyle of fontDefinition.styles) {
                        if (fontStyle.fontFaceName === regularFontFaceName) {
                            this.update({
                                [`font${style}FontId`]: fontId,
                                [`font${style}Weight`]: fontStyle.weight
                            });
                        }

                        if (fontStyle.fontFaceName === emphasizedFontFaceName) {
                            this.update({
                                [`font${style}BoldWeight`]: fontStyle.weight
                            });
                        }
                    }
                }

                // Custom font
                if (!this.get(`font${style}FontId`)) {
                    try {
                        const [fontId, fontStyle] = regularFontFaceName.split("/");
                        const emphasizedFontStyle = emphasizedFontFaceName.split("/")[1];
                        this.update({
                            [`font${style}FontId`]: fontId,
                            [`font${style}Weight`]: fontStyle === "bold" ? 700 : 400,
                            [`font${style}BoldWeight`]: emphasizedFontStyle === "bold" ? 700 : 400
                        });
                    } catch {
                        // Handle silently
                    }
                }

                // Cleaning up obsolete fields
                this.update({
                    [`font${style}`]: null,
                    [`font${style}Regular`]: null,
                    [`font${style}Italic`]: null,
                    [`font${style}Emphasized`]: null,
                    [`font${style}EmphasizedItalic`]: null
                });
            }
        }
    },

    migrateToStyleSets() {
        console.log("Migrating theme to style sets");

        this.hasMigrationChanges = true;

        const colors = this.get("colors");

        const migratedColors = [{
            type: "color",
            value: colors.theme.value,
            styleSet: "dark"
        }, ...Object.keys(colors).filter(color => color.startsWith("accent")).map(color => ({
            type: "color",
            value: colors[color].value,
            styleSet: tinycolor(colors[color].value).isDark() ? "dark" : "light"
        }))];

        const convertColor = color => {
            return color === "theme" ? "color-0" : color.replace("accent", "color-");
        };

        const migratedCollectionColors = this.get("collectionColors")?.map(collectionColor => ({
            ...collectionColor,
            colors: collectionColor.colors.map(color => convertColor(color)),
            startColor: collectionColor.startColor ? convertColor(collectionColor.startColor) : null,
            endColor: collectionColor.endColor ? convertColor(collectionColor.endColor) : null,
        }));

        const migratedChartColors = Object.keys(colors).filter(color => color.startsWith("chart")).map(chartColor => ({
            type: "color",
            value: colors[chartColor].value,
            styleSet: tinycolor(colors[chartColor].value).isDark() ? "dark" : "light"
        }));

        const shapeStyles = getDefaultShapeStyle(this.get("styleElementStyle"));
        let iconStyles;
        if (this.get("styleShape") == "none") {
            iconStyles = {
                defaultColor: "color-0",
                fillOpacity: 0,
                strokeWidth: 0,
                iconColor: null,
                iconStyle: this.get("styleIconStyle"),
                iconScale: 1
            };
        } else {
            iconStyles = {
                ...shapeStyles,
                defaultColor: "color-0",
                shape: this.get("styleShape"),
                iconStyle: this.get("styleIconStyle"),
                iconScale: 0.75
            };
        }

        const containers = [{
            type: "color",
            value: colors.background_accent.value,
            styleSet: tinycolor(colors.background_accent.value).isDark() ? "dark" : "light"
        }];

        this.update({
            textStyles: this.migrateTextStyles(),
            backgrounds: [{
                id: "light",
                name: "Light",
                type: "color",
                value: colors.background_light?.value ?? "#ffffff",
                styleSet: "light"
            }, {
                id: "dark",
                name: "Dark",
                type: "color",
                value: colors.background_dark?.value ?? "#000000",
                styleSet: "dark"
            }, {
                id: "bg-accent",
                name: "Accent",
                type: "color",
                value: colors.background_accent?.value ?? "#000000",
                styleSet: "light"
            }, ...Object.keys(colors).filter(color => color.startsWith("bg")).map(color => ({
                id: color,
                name: color,
                ...colors[color],
                styleSet: tinycolor(colors[color].value).isDark() ? "color-dark" : "color-light"
            }))],
            styleSets: [{
                id: "light",
                name: "For Light Backgrounds",
                primary: colors.primary_dark,
                secondary: colors.secondary_dark,
                hyperlink: colors.hyperlink,
                colors: migratedColors,
                collectionColors: migratedCollectionColors,
                chartColors: migratedChartColors,
                containers,
                shapeStyles,
                iconStyles,
                calloutStyleSet: "light",
            }, {
                id: "dark",
                name: "For Dark Backgrounds",
                primary: colors.primary_light,
                secondary: colors.secondary_light,
                hyperlink: colors.hyperlink,
                colors: migratedColors,
                collectionColors: migratedCollectionColors,
                chartColors: migratedChartColors,
                containers,
                shapeStyles,
                iconStyles,
                calloutStyleSet: "dark",
            }, {
                id: "color-light",
                name: "For Light Color Backgrounds",
                primary: colors.primary_dark,
                secondary: colors.secondary_dark,
                hyperlink: colors.hyperlink,
                containers,
                colors: [{
                    type: "color",
                    value: "#ffffff",
                    styleSet: "light"
                }],
                chartColors: migratedChartColors,
                collectionColors: [],
                shapeStyles,
                iconStyles,
                calloutStyleSet: "light",
            }, {
                id: "color-dark",
                name: "For Dark Color Backgrounds",
                primary: colors.primary_light,
                secondary: colors.secondary_light,
                hyperlink: colors.hyperlink,
                colors: [{
                    type: "color",
                    value: "#ffffff",
                    styleSet: "light"
                }],
                collectionColors: [],
                chartColors: migratedChartColors,
                containers,
                shapeStyles,
                iconStyles,
                calloutStyleSet: "dark",
            }]
        });
    },

    migrateTextStyles() {
        if (this.get("textBlockStyles")) {
            return this.get("textBlockStyles");
        } else {
            const migrateTextStyles = type => {
                if (this.get(`font${type}FontId`)) {
                    return {
                        type,
                        fontId: this.get(`font${type}FontId`),
                        fontWeight: this.get(`font${type}Weight`),
                        fontItalic: this.get(`font${type}Italic`),
                        fontScaling: this.get(`font${type}Scaling`),
                        letterSpacing: this.get(`font${type}LetterSpacing`),
                        lineHeight: this.get(`font${type}LineHeight`),
                        textTransform: this.get(`font${type}TextTransform`),
                        fontColor: type == "Body" ? "secondary" : "primary",
                    };
                }
            };

            return {
                default: {
                    heading: migrateTextStyles("Header"),
                    title: migrateTextStyles("Title"),
                    body: migrateTextStyles("Body"),
                    label: migrateTextStyles("Label") ?? migrateTextStyles("Title"),
                    bulleted: {
                        indentLevel: [{
                            ...migrateTextStyles("Title"),
                            bulletStyle: {
                                bullet: "circle",
                                color: "color-0",
                                scale: 0.3,
                                offset: 0,
                            }
                        }, {
                            ...migrateTextStyles("Body"),
                            bulletStyle: {
                                bullet: "circle",
                                color: "primary",
                                scale: 0.3,
                                offset: 0,
                            }
                        }]
                    }
                }
            };
        }
    },

    async loadFonts(styleSheet) {
        // Composing style sheets
        styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("header", "Header"));
        styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("display", "Header"));
        styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("title", "Title"));
        styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("body", "Body"));
        if (this.has("fontLabelFontId")) {
            styleSheet = this.mergeCssIntoStylesheet(styleSheet, await this.buildCustomFontCss("label", "Label"));
        }

        // optimistically preload the fonts instead of waiting for BaseElement (plus the emphasized header font is outside styles and wouldn't be loaded)
        const headerFont = await fontManager.loadFont(this.get("fontHeaderFontId"));
        await Promise.all(headerFont.styles.map(style => style.loadCssFont()));
        if (this.has("fontHeaderEmphasizedFontId")) {
            const headerEmphasizedFont = await fontManager.loadFont(this.get("fontHeaderEmphasizedFontId"));
            await Promise.all(headerEmphasizedFont.styles.map(style => style.loadCssFont()));
        }
        const titleFont = await fontManager.loadFont(this.get("fontTitleFontId"));
        await Promise.all(titleFont.styles.map(style => style.loadCssFont()));
        const bodyFont = await fontManager.loadFont(this.get("fontBodyFontId"));
        await Promise.all(bodyFont.styles.map(style => style.loadCssFont()));

        if (this.has("fontLabelFontId")) {
            const labelFont = await fontManager.loadFont(this.get("fontLabelFontId"));
            await Promise.all(labelFont.styles.map(style => style.loadCssFont()));
        }

        return styleSheet;
    },

    /**
     * Sets obsolete font variables for migration
     */
    setObsoleteFontVariables(fontType, fontFamily, variables) {
        const capitalizedFontType = formatter.capitalizeFirstLetter(fontType);
        const fontUpdates = {
            [`font${capitalizedFontType}`]: fontFamily,
            [`font${capitalizedFontType}Regular`]: variables[`${fontType}FontRegular`],
            [`font${capitalizedFontType}Italic`]: variables[`${fontType}FontItalic`],
            [`font${capitalizedFontType}Emphasized`]: variables[`${fontType}FontEmphasized`],
            [`font${capitalizedFontType}EmphasizedItalic`]: variables[`${fontType}FontEmphasizedItalic`],
            [`font${capitalizedFontType}LineHeight`]: variables[`${fontType}FontLineHeight`],
            [`font${capitalizedFontType}LetterSpacing`]: variables[`${fontType}FontLetterSpacing`],
            [`font${capitalizedFontType}TextTransform`]: variables[`${fontType}FontTextTransform`] || "auto",
            [`font${capitalizedFontType}Scaling`]: variables[`${fontType}FontScaling`] || 100
        };

        if (variables[`${fontType}FontStrokeThickness`]) {
            fontUpdates[`font${capitalizedFontType}StrokeThickness`] = variables[`${fontType}FontStrokeThickness`];
        }
        this.update(fontUpdates);
    },

    async buildCustomFontCss(fontKey, textStyle) {
        return `
        $${fontKey}FontId: ${this.get(`font${textStyle}FontId`)};
        $${fontKey}FontItalic: ${this.get(`font${textStyle}FontItalic`) ?? false};
        $${fontKey}FontWeight: ${this.get(`font${textStyle}Weight`)};
        ${this.get(`font${textStyle}BoldWeight`) ? `$${fontKey}FontBoldWeight: ${this.get(`font${textStyle}BoldWeight`)};` : ""}
        $${fontKey}FontLineHeight: ${this.get(`font${textStyle}LineHeight`)};
        $${fontKey}FontLetterSpacing: ${this.get(`font${textStyle}LetterSpacing`)};
        $${fontKey}FontTextTransform: ${this.get(`font${textStyle}TextTransform`)};
        $${fontKey}FontScaling: ${this.get(`font${textStyle}Scaling`)};
        $${fontKey}FontStrokeThickness: ${this.get(`font${textStyle}StrokeThickness`)};
        `;
    },

    async loadStyleSheet(cssFilePath, baseStyleSheet = null) {
        try {
            const cssText = await fetch(cssFilePath).then(response => response.text());
            return this.mergeCssIntoStylesheet(baseStyleSheet, cssText);
        } catch {
            // Fallback to default
            return baseStyleSheet;
        }
    },

    mergeCssIntoStylesheet(styleSheet = null, css) {
        if (_.isEmpty(css)) {
            return styleSheet ?? new StyleSheet(this, "");
        }

        if (styleSheet) {
            _.merge(styleSheet, new StyleSheet(this, css));
        } else {
            styleSheet = new StyleSheet(this, css);
        }
        return styleSheet;
    },

    sync: function() {
        //void
    }
});

const BuiltInThemes = Backbone.Collection.extend({
    model: Theme,

    initialize: function() {
        Object.values(themes).forEach(theme => {
            this.add(new Theme(theme, { disconnected: true, autoLoad: false }));
        });
    }
});

export { BuiltInThemes, IconStyle, PaintStyle, PREDEFINED_PALETTES, ShapeColorStyle, ShapeType, Theme, ThemeStyleWeight };
